import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Select, Input, Button, Space, Tooltip, Typography, Statistic, Row, Col, Modal, Tabs, message } from 'antd';
import { ReloadOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import NodeIpsPanel from './NodeIpsPanel';
import IpSubPools from './IpSubPools';
import DdosTag from './DdosTag';

const { Text } = Typography;
const HEALTH_COLOR = { up: 'success', down: 'error', unknown: 'default' };
const fmtTime = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleString() : '-');

/** CDN IP 池:全网 IP 清单 + 监控(健康 / 票数 / 所在子池);子池管理在第二个标签 */
const IpPool = () => {
    const { t } = useTranslation();
    const [data, setData] = useState({ items: [], counts: {}, nodes: [] });
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ node_id: undefined, family: undefined, health: undefined, pool_id: undefined, q: '' });
    const [pools, setPools] = useState([]);
    const [editNode, setEditNode] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''));
        const r = await cdnNodeOpsService.getIpPool(params);
        setLoading(false);
        if (r.success) setData(r.data || { items: [], counts: {}, nodes: [] });
        else message.error(r.error || t('Failed to load'));
    }, [filters, t]);

    const loadPools = useCallback(async () => {
        const r = await cdnNodeOpsService.listIpPools();
        if (r.success) setPools(r.data?.pools || []);
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadPools(); }, [loadPools]);
    useEffect(() => {
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
    const c = data.counts || {};
    const healthLabel = useMemo(() => ({ up: t('Up'), down: t('Down'), unknown: t('Unknown') }), [t]);

    const columns = [
        {
            title: t('Node'), dataIndex: 'node_name',
            render: (n, r) => (
                <Space size={4}>
                    <a onClick={() => setEditNode({ id: r.node_id, name: n })}>{n}</a>
                    {r.node_status !== 'active' && <Tag>{r.node_status}</Tag>}
                </Space>
            ),
        },
        {
            title: 'IP', dataIndex: 'ip',
            render: (ip, r) => (
                <Space size={4} wrap>
                    <Text code>{ip}</Text>
                    <DdosTag gbps={r.ddos_gbps} />
                    {r.family === 'ipv6' && <Tag>IPv6</Tag>}
                    {r.is_primary && <Tag>{t('Primary')}</Tag>}
                    {r.anycast && <Tag color="purple">Anycast</Tag>}
                    {r.in_dns && <Tag color="blue">DNS</Tag>}
                </Space>
            ),
        },
        {
            title: t('IP pools'), dataIndex: 'pools',
            render: (ps) => (ps?.length
                ? <Space size={[0, 4]} wrap>{ps.map((p) => <Tag key={p.id} color={p.published ? 'purple' : 'default'}>{p.name}</Tag>)}</Space>
                : <Text type="secondary">-</Text>),
        },
        {
            title: t('Health'), dataIndex: 'health',
            render: (h, r) => {
                const tip = r.results?.length
                    ? r.results.map((x) => (
                        <div key={x.v}>
                            {x.ok ? '✓' : '✗'} {x.v}{x.x ? ` (${t('ignored')})` : ''} · {x.ok ? `${x.ms}ms` : (x.err || x.code)}
                        </div>
                    ))
                    : t('No probe results yet');
                return (
                    <Tooltip title={tip}>
                        <Space size={4}>
                            <Tag color={HEALTH_COLOR[h]}>{healthLabel[h] || h}</Tag>
                            {r.votes > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{r.votes_ok}/{r.votes}</Text>}
                        </Space>
                    </Tooltip>
                );
            },
        },
        { title: t('Last checked'), dataIndex: 'last_checked_at', render: fmtTime },
        { title: t('Changed'), dataIndex: 'health_changed_at', render: fmtTime },
    ];

    const ipTab = (
        <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={4}><Statistic title={t('Total')} value={c.total || 0} /></Col>
                <Col xs={12} sm={4}><Statistic title="IPv6" value={c.ipv6 || 0} /></Col>
                <Col xs={12} sm={4}><Statistic title={t('IP pools')} value={c.pooled || 0} /></Col>
                <Col xs={12} sm={4}><Statistic title="DNS" value={c.in_dns || 0} /></Col>
                <Col xs={12} sm={4}><Statistic title={t('Up')} value={c.up || 0} valueStyle={{ color: '#52c41a' }} /></Col>
                <Col xs={12} sm={4}><Statistic title={t('Down')} value={c.down || 0} valueStyle={{ color: c.down ? '#ff4d4f' : undefined }} /></Col>
            </Row>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select allowClear showSearch optionFilterProp="label" placeholder={t('Node')} style={{ width: 200 }}
                    value={filters.node_id} onChange={set('node_id')}
                    options={(data.nodes || []).map((n) => ({ value: n.id, label: n.name }))} />
                <Select allowClear placeholder={t('IP pools')} style={{ width: 150 }} value={filters.pool_id} onChange={set('pool_id')}
                    options={[{ value: 'none', label: t('Not in a pool') }, ...pools.map((p) => ({ value: String(p.id), label: p.name }))]} />
                <Select allowClear placeholder="IPv4 / IPv6" style={{ width: 120 }} value={filters.family} onChange={set('family')}
                    options={[{ value: 'ipv4', label: 'IPv4' }, { value: 'ipv6', label: 'IPv6' }]} />
                <Select allowClear placeholder={t('Health')} style={{ width: 120 }} value={filters.health} onChange={set('health')}
                    options={Object.entries(healthLabel).map(([value, label]) => ({ value, label }))} />
                <Input.Search allowClear placeholder="IP" style={{ width: 200 }} onSearch={set('q')} />
            </Space>
            <Table size="small" rowKey="id" columns={columns} dataSource={data.items} loading={loading}
                pagination={{ pageSize: 50, showSizeChanger: true }} scroll={{ x: 'max-content' }} />
        </>
    );

    return (
        <Card
            title={<Space><GlobalOutlined />{t('CDN IP pool')}</Space>}
            extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />}
        >
            <Tabs
                items={[
                    { key: 'ips', label: 'IP', children: ipTab },
                    { key: 'pools', label: t('IP pools'), children: <IpSubPools onChanged={() => { load(); loadPools(); }} /> },
                ]}
            />
            <Modal
                title={<Space><GlobalOutlined />{editNode?.name} · IP</Space>}
                open={!!editNode}
                onCancel={() => { setEditNode(null); load(); }}
                footer={null}
                width={760}
                destroyOnClose
            >
                {editNode && <NodeIpsPanel nodeId={editNode.id} />}
            </Modal>
        </Card>
    );
};

export default IpPool;
